import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { userAPI, walletAPI } from '../../services/api';
import { useAuth } from '../context/AuthContext'; // Add this import
import '../../styles/admin/CreditDebit.css';

const CreditDebit = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const { user: currentUser } = useAuth(); // Get current logged in user
    const [targetUser, setTargetUser] = useState(null);
    const [operation, setOperation] = useState('credit');
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [insufficientBalance, setInsufficientBalance] = useState(false);

    useEffect(() => {
        loadTargetUser();
    }, [userId]);

    useEffect(() => {
        // Check if amount exceeds current user's balance (for non-admin credit operations)
        if (operation === 'credit' && currentUser.role !== 'admin' && amount) {
            const amountNum = parseFloat(amount);
            if (!isNaN(amountNum) && amountNum > currentUser.balance) {
                setInsufficientBalance(true);
            } else {
                setInsufficientBalance(false);
            }
        } else {
            setInsufficientBalance(false);
        }
    }, [amount, operation, currentUser]);

    const loadTargetUser = async () => {
        try {
            const response = await userAPI.getUserDetails(userId);
            setTargetUser(response.data);
        } catch (error) {
            setError('Error loading user');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const amountNum = parseFloat(amount);
            if (isNaN(amountNum) || amountNum <= 0) {
                throw new Error('Please enter a valid amount');
            }

            // For non-admin credit, check balance again before submitting
            if (operation === 'credit' && currentUser.role !== 'admin') {
                if (amountNum > currentUser.balance) {
                    throw new Error(`Insufficient balance. Your wallet: ₹${currentUser.balance.toLocaleString()}`);
                }
            }

            const data = {
                userId,
                amount: amountNum,
                description: description || `${operation} by ${currentUser.username}`
            };

            if (operation === 'credit') {
                await walletAPI.credit(data);
                alert(`Balance credited successfully! ₹${amountNum} deducted from your wallet.`);
            } else {
                await walletAPI.debit(data);
                alert(`Balance debited successfully! ₹${amountNum} added to your wallet.`);
            }

            navigate(`/users/${userId}`);
        } catch (error) {
            setError(error.response?.data?.message || error.message);
        } finally {
            setLoading(false);
        }
    };

    if (!targetUser || !currentUser) {
        return <div className="loading">Loading...</div>;
    }

    const isAdmin = currentUser.role === 'admin';

    return (
        <div className="credit-debit">
            <h1>Manage Wallet - {targetUser.username}</h1>
            
            {/* Current User's Wallet Balance (for non-admin) */}
            {!isAdmin && (
                <div className={`wallet-balance ${currentUser.balance < parseFloat(amount || 0) ? 'insufficient' : 'sufficient'}`}>
                    <h3>Your Wallet Balance</h3>
                    <p className="balance-amount">₹{currentUser.balance?.toLocaleString()}</p>
                    {operation === 'credit' && (
                        <p className="balance-note">
                            {insufficientBalance 
                                ? `⚠️ Insufficient balance! You need ₹${(parseFloat(amount) - currentUser.balance).toLocaleString()} more.`
                                : 'This amount will be deducted from your wallet when you credit'}
                        </p>
                    )}
                    {operation === 'debit' && (
                        <p className="balance-note">
                            This amount will be added to your wallet when you debit
                        </p>
                    )}
                </div>
            )}
            
            {/* Admin Note */}
            {isAdmin && (
                <div className="admin-note">
                    <p>✨ Admin: You have unlimited balance</p>
                </div>
            )}
            
            {/* Target User's Current Balance */}
            <div className="current-balance">
                <h3>{targetUser.username}'s Current Balance</h3>
                <p className="balance-amount">₹{targetUser.balance?.toLocaleString()}</p>
            </div>
            
            {error && (
                <div className="alert alert-danger">{error}</div>
            )}
            
            <div className="operation-tabs">
                <button
                    className={`tab ${operation === 'credit' ? 'active' : ''}`}
                    onClick={() => setOperation('credit')}
                    disabled={loading}
                >
                    Credit
                </button>
                <button
                    className={`tab ${operation === 'debit' ? 'active' : ''}`}
                    onClick={() => setOperation('debit')}
                    disabled={loading}
                >
                    Debit
                </button>
            </div>
            
            <form onSubmit={handleSubmit} className="wallet-form">
                <div className="form-group">
                    <label>Amount (₹)</label>
                    <input
                        type="number"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="Enter amount"
                        min="1"
                        step="0.01"
                        required
                        disabled={loading}
                        className={insufficientBalance ? 'input-error' : ''}
                    />
                    {!isAdmin && operation === 'credit' && currentUser.balance > 0 && (
                        <small className="help-text">
                            Max you can credit: ₹{currentUser.balance.toLocaleString()}
                        </small>
                    )}
                </div>
                
                <div className="form-group">
                    <label>Description (Optional)</label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Enter description"
                        rows="3"
                        disabled={loading}
                    />
                </div>
                
                {/* Transaction Summary */}
                {amount && !isNaN(parseFloat(amount)) && parseFloat(amount) > 0 && (
                    <div className="transaction-summary">
                        <h4>Transaction Summary</h4>
                        {operation === 'credit' && (
                            <>
                                <p>💸 You will credit: <strong>₹{parseFloat(amount).toLocaleString()}</strong> to {targetUser.username}</p>
                                {!isAdmin && (
                                    <p>💰 This will be deducted from your wallet</p>
                                )}
                                <p className={!isAdmin && insufficientBalance ? 'text-danger' : 'text-success'}>
                                    {!isAdmin && `Your balance after: ₹${(currentUser.balance - parseFloat(amount)).toLocaleString()}`}
                                </p>
                            </>
                        )}
                        {operation === 'debit' && (
                            <>
                                <p>💸 You will debit: <strong>₹{parseFloat(amount).toLocaleString()}</strong> from {targetUser.username}</p>
                                {!isAdmin && (
                                    <p>💰 This will be added to your wallet</p>
                                )}
                                {!isAdmin && (
                                    <p className="text-success">
                                        Your balance after: ₹{(currentUser.balance + parseFloat(amount)).toLocaleString()}
                                    </p>
                                )}
                            </>
                        )}
                    </div>
                )}
                
                <div className="form-actions">
                    <button 
                        type="button" 
                        className="btn btn-secondary"
                        onClick={() => navigate(`/users/${userId}`)}
                        disabled={loading}
                    >
                        Cancel
                    </button>
                    <button 
                        type="submit" 
                        className={`btn btn-${operation}`}
                        disabled={loading || (operation === 'credit' && !isAdmin && insufficientBalance)}
                    >
                        {loading ? 'Processing...' : `${operation} Balance`}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default CreditDebit;